import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { User } from '@services/user.interface';
import { PipesModule } from '@pipes/pipes.module';
import { VideoPlayerModule } from '@components/video-player/video-player.module';
import {
  EducationOverlayModule,
  BookmarkOverlayModule,
  BoostYourKnowledgeOverlayModule,
  BuildYourCommunityPartOneOverlayModule,
  BuildYourCommunityPartTwoOverlayModule,
  EventsOverlayModule,
  SummaryOverlayModule,
  WelcomeOverlayModule,
} from '@overlays/_index';
import { Router, RouterModule } from '@angular/router';
import { Observable, Subscription } from 'rxjs';

import { StartOverlayModule } from '../../overlays/start-overlay/start-overlay.module';
import { ContactUsOverlayModule } from '../../overlays/contact-us-overlay/contact-us-overlay.module';
import { Marker, VideoPlayerComponent } from '@components/_index';
import { CustomerMarkerOverlayType } from '@enums/overlay-types.enums';
import { CustomerMarkers } from '@enums/marker-types.enums';
import {
  EDUCATION_MARKERS,
  BOOKMARK_KNOW_MARKERS,
  BOOST_YOUR_KNOWLEDGE_MARKERS,
  COMMUNITY_PART_1_MARKERS,
  COMMUNITY_PART_2_MARKERS,
  CONTACT_US_MARKERS,
  EVENTS_MARKERS,
  RELAX_MARKERS,
  START_MARKERS,
  SUMMARY_MARKERS,
  WELCOME_MARKERS,
} from '@dictionaries/customer-markers-dictionary';
import { trigger, transition, style, animate } from '@angular/animations';
import { HarmonizedShapesModule } from '../../overlays/harmonized-shapes/harmonized-shapes.module';
import { PlatformAPIClientAuthorizationService } from '@services/platform-api-client-authorization.service';
import { KeplerSlapfiveService } from '@services/kepler-slapfive/kepler-slapfive.service';

interface UserState {
  data: User | null;
  loading: boolean;
  error: string | null;
}
import { RelaxOverlayModule } from '../../overlays/relax-overlay/relax-overlay.module';
import { adaptUser } from '@adapters/user.adapter';
import { GenesysCloudUser } from '@services/genesys-cloud-user.interface';
import { HomePlayButtonModule } from '../../components/home-play-button/home-play-button.module';
import { AvatarModule } from '@components/avatar/avatar.module';
import { ViewportSize } from '@services/viewport-size.interface';
import { AppIconRegistryService } from '@services/app-icon-registry.service';

@Component({
  selector: 'app-customer-video-home',
  animations: [
    trigger('fadeOut', [
      transition(':leave', [style({ opacity: 1 }), animate('500ms ease-out', style({ opacity: 0 }))]),
    ]),
    trigger('slideFromRightAndFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }), // Start from right
        animate(
          '600ms ease-out',
          style({
            opacity: 1,
            transform: 'translateX(0)',
          }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateX(0)' }),
        animate(
          '500ms ease-out',
          style({
            opacity: 0,
            transform: 'translateX(-100%)', // Exit to left
          }),
        ),
      ]),
    ]),
    trigger('slideFromLeftAndFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-100%)' }), // Start from left
        animate(
          '600ms ease-out',
          style({
            opacity: 1,
            transform: 'translateX(0)', // Move to center
          }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateX(0)' }),
        animate(
          '500ms ease-out',
          style({
            opacity: 0,
            transform: 'translateX(100%)', // Exit to right
          }),
        ),
      ]),
    ]),
    trigger('slideFromTopAndFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-100%)' }), // Start from top
        animate(
          '600ms ease-out',
          style({
            opacity: 1,
            transform: 'translateY(0)', // Move to normal position
          }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0)' }),
        animate(
          '500ms ease-out',
          style({
            opacity: 0,
            transform: 'translateY(100%)', // Exit to bottom
          }),
        ),
      ]),
    ]),
  ],
  imports: [
    RouterModule,
    EducationOverlayModule,
    BookmarkOverlayModule,
    BoostYourKnowledgeOverlayModule,
    BuildYourCommunityPartOneOverlayModule,
    BuildYourCommunityPartTwoOverlayModule,
    ContactUsOverlayModule,
    EventsOverlayModule,
    PipesModule,
    SummaryOverlayModule,
    WelcomeOverlayModule,
    VideoPlayerModule,
    StartOverlayModule,
    HarmonizedShapesModule,
    RelaxOverlayModule,
    AvatarModule,
    HomePlayButtonModule,
  ],
  templateUrl: './customer-video-home.component.html',
  styleUrl: './customer-video-home.component.scss',
})
export class CustomerVideoHomeComponent implements OnInit, OnDestroy {
  @ViewChild(VideoPlayerComponent) videoPlayer!: VideoPlayerComponent;

  private authService = inject(PlatformAPIClientAuthorizationService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private keplerSlapfiveService = inject(KeplerSlapfiveService);
  private iconRegistryService = inject(AppIconRegistryService);
  videoUrl = 'https://csd-static-assets-572708336319-us-east-1.s3.us-east-1.amazonaws.com/assets/GC_background+_v3.mp4';
  message = `We've prepared a personalized video just for you.`;

  // NOTE: SlapFive Activity ids are the titles, so the titles must exist and match.
  slapFiveActivity = 'Watch Personalized Video';

  // Expose the enum to the template
  OverlayType = CustomerMarkerOverlayType;
  subs: Subscription[] = [];
  isMobile$: Observable<boolean>;

  userState = signal<UserState>({
    data: null,
    loading: true,
    error: null,
  });

  markers: Marker[] = [
    ...START_MARKERS,
    ...WELCOME_MARKERS,
    ...RELAX_MARKERS,
    ...BOOKMARK_KNOW_MARKERS,
    ...BOOST_YOUR_KNOWLEDGE_MARKERS,
    ...COMMUNITY_PART_1_MARKERS,
    ...COMMUNITY_PART_2_MARKERS,
    ...EVENTS_MARKERS,
    ...EDUCATION_MARKERS,
    ...CONTACT_US_MARKERS,
    ...SUMMARY_MARKERS,
  ];

  // Active overlay signal to handle switching overlays
  public activeOverlay = signal<CustomerMarkerOverlayType | null>(CustomerMarkerOverlayType.START);
  public showPlayButtonSignal = signal(true);
  isPlayingFirstTime = true;

  currentViewportSize: ViewportSize | null = null;

  constructor() {}

  /**
   * OnInit to handle routing from params to determine user or user role type.
   */
  ngOnInit(): void {
    this.iconRegistryService.start();
    this.initializeUserData();
  }

  /**
   * Initialize user data based on route params
   */
  private initializeUserData(): void {
    const tempUserData = sessionStorage.getItem('temp_user_data');

    if (tempUserData) {
      try {
        // Use temp user data from no-signin form
        const userData = JSON.parse(tempUserData);
        const tempUser: GenesysCloudUser = {
          id: 'temp-user',
          name: `${userData.firstName} ${userData.lastName}`,
          email: userData.email,
          username: userData.email,
          title: userData.title,
          department: '',
          state: 'active',
          version: 1,
          acdAutoAnswer: false,
          images: [],
          addresses: [],
          locations: [],
          languages: [],
          languagePreference: 'en-US',
          organization: {
            id: 'temp-org',
            name: userData.company,
            defaultLanguage: 'en',
            defaultCountryCode: 'US',
            thirdPartyOrgName: userData.company,
            domain: 'temp',
            version: 1,
            state: 'active',
            defaultSiteId: 'temp-site',
            thirdPartyOrgId: 'temp-id',
            deletable: false,
            supportURI: '',
            voicemailEnabled: false,
            productPlatform: 'GenesysCloud',
            selfUri: '/api/v2/organizations/temp',
            features: {
              chat: false,
              contactCenter: false,
              custserv: false,
              directory: true,
              informalPhotos: false,
              purecloud: false,
              realtimeCIC: false,
              unifiedCommunications: false,
              purecloudVoice: false,
              hipaa: false,
              xmppFederation: false,
              pci: false,
            },
          },
        };

        this.updateUserState({ data: adaptUser(tempUser), loading: false, error: null });
      } catch (error) {
        console.error('Invalid temp user data:', error);
        sessionStorage.removeItem('temp_user_data');
        this.updateUserState({ data: null, loading: false, error: 'Invalid user data' });
      }
    } else {
      // Use auth service for authenticated users
      this.authService.getCurrentUser().subscribe((user: GenesysCloudUser) => {
        this.updateUserState({ data: adaptUser(user), loading: false, error: null });
      });
    }
  }

  /**
   * Unsubscribe subscriptions on destroy.
   */
  ngOnDestroy(): void {
    this.subs?.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Update user state
   */
  private updateUserState(state: Partial<UserState>): void {
    this.userState.update((currentState) => ({
      ...currentState,
      ...state,
    }));
  }

  // Getter methods for template use
  get userData(): User | null {
    return this.userState().data;
  }

  get isLoading(): boolean {
    return this.userState().loading;
  }

  get errorMessage(): string | null {
    return this.userState().error;
  }

  /**
   * Set the default welcome overlay to be turn off or be hidden on play start.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPlayStarted(isPlaying: boolean): void {
    if (this.isPlayingFirstTime) {
      this.activeOverlay.set(null);
      this.hidePlayButton();
      this.isPlayingFirstTime = false;
    }
  }

  /**
   * Handle and the video resize in conjuction with the browser size
   * @param size - Video player view port size
   */
  onVideoResize(size: ViewportSize): void {
    this.currentViewportSize = size;
    // console.log('Video size updated:', size);
    this.cdr.detectChanges();
  }

  // This is a method to set the signal to true
  private showPlayButton(): void {
    this.showPlayButtonSignal.set(true);
  }

  // This is a method to set the signal to false
  private hidePlayButton(): void {
    this.showPlayButtonSignal.set(false);
  }

  /**
   * Set the default start overlay to be turn off or be hidden on play start.
   */
  onReplay(): void {
    this.activeOverlay.set(CustomerMarkerOverlayType.START);
    this.videoPlayer.replayVideo();
  }

  /**
   * Set the active overlay based on the marker.
   * @param marker The marker that was detected.
   */
  onMarkerChange(marker: Marker): void {
    switch (marker.text) {
      case CustomerMarkers.START:
        this.activeOverlay.set(CustomerMarkerOverlayType.START);
        break;
      case CustomerMarkers.WELCOME:
        this.activeOverlay.set(CustomerMarkerOverlayType.WELCOME);
        break;
      case CustomerMarkers.RELAX:
        this.activeOverlay.set(CustomerMarkerOverlayType.RELAX);
        break;
      case CustomerMarkers.BOOKMARK_KNOW:
        this.activeOverlay.set(CustomerMarkerOverlayType.BOOKMARK_KNOW);
        break;
      case CustomerMarkers.BOOST_YOUR_KNOWLEDGE:
        this.activeOverlay.set(CustomerMarkerOverlayType.BOOST_YOUR_KNOWLEDGE);
        break;
      case CustomerMarkers.COMMUNITY_PART_1:
        this.activeOverlay.set(CustomerMarkerOverlayType.COMMUNITY_PART_1);
        break;
      case CustomerMarkers.COMMUNITY_PART_2:
        this.activeOverlay.set(CustomerMarkerOverlayType.COMMUNITY_PART_2);
        break;
      case CustomerMarkers.EVENTS:
        this.activeOverlay.set(CustomerMarkerOverlayType.EVENTS);
        break;
      case CustomerMarkers.EDUCATION:
        this.activeOverlay.set(CustomerMarkerOverlayType.EDUCATION_COURSE);
        break;
      case CustomerMarkers.CONTACT_US:
        this.activeOverlay.set(CustomerMarkerOverlayType.CONTACT_US);
        break;
      case CustomerMarkers.SUMMARY:
        this.activeOverlay.set(CustomerMarkerOverlayType.SUMMARY);
        this.postActivityToSlapFive();
        break;
      default:
        this.activeOverlay.set(null);
    }
  }

  /**
   * Handles marker clicks and toggles video playback
   * @param event The marker click event
   */
  onMarkerClicked(marker: Marker): void {
    console.log('Marker clicked in parent:', marker);
  }

  /**
   * Handle link clicks in overlays when opening links.
   * This will pause the video and maintain pause state when clicking links.
   */
  handleOverlayLinkClick(): void {
    if (this.videoPlayer && !this.videoPlayer.isPaused()) {
      this.videoPlayer.togglePlay();
    }
  }

  /**
   * Handle Summary marker activity by calling kepler-slapfive service
   */
  private postActivityToSlapFive(): void {
    const userData = this.userData;

    if (userData) {
      const isTempUser = userData.id === 'temp-user';
      this.keplerSlapfiveService
        .addActivity(
          userData.email,
          userData.firstName,
          userData.lastName,
          userData?.company,
          this.slapFiveActivity,
          isTempUser,
        )
        .subscribe({
          next: (response) => {
            console.debug('Activity added successfully:', response);
          },
          error: (error) => {
            console.error('Error adding activity:', error);
          },
        });
    }
  }
}
